import { useCallback, useState } from "react";
import { toast } from "react-toastify";

interface ErrorState {
  message: string;
  code?: string | number;
  field?: string;
}

interface UseErrorHandlerReturn {
  error: ErrorState | null;
  isLoading: boolean;
  clearError: () => void;
  handleError: (error: unknown, context?: string) => void;
  executeWithErrorHandling: <T>(
    asyncFn: () => Promise<T>,
    successMessage?: string,
    context?: string
  ) => Promise<T | null>;
}

/**
 * Hook personalizado para tratamento robusto de erros
 */
export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [error, setError] = useState<ErrorState | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((error: unknown, context = "Operação") => {
    let errorMessage = "Erro inesperado ocorreu";
    let errorCode: string | number | undefined;
    let errorField: string | undefined;

    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    } else if (error && typeof error === "object") {
      const errorObj = error as Record<string, unknown>;
      errorMessage =
        (errorObj.message as string) || (errorObj.error as string) || "Erro desconhecido";
      errorCode = (errorObj.code as string | number) || (errorObj.status as string | number);
      errorField = errorObj.field as string;
    }

    // Verificar se é erro de token inválido/expirado
    const isAuthError =
      errorCode === 401 ||
      (errorMessage.toLowerCase().includes("token") &&
        (errorMessage.toLowerCase().includes("inválido") ||
          errorMessage.toLowerCase().includes("expirado") ||
          errorMessage.toLowerCase().includes("invalid") ||
          errorMessage.toLowerCase().includes("expired")));

    if (isAuthError) {
      // Limpar dados de autenticação
      localStorage.removeItem("authUser");
      localStorage.removeItem("davData");
      localStorage.removeItem("token");

      // Redirecionar para login sem mostrar toast de erro
      globalThis.location.href = "/login";
      return;
    }

    const errorState: ErrorState = {
      message: errorMessage,
      code: errorCode,
      field: errorField,
    };

    setError(errorState);

    // Toast de erro com contexto
    toast.error(`${context}: ${errorMessage}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
    });

    console.error(`[${context}] Error:`, error);
  }, []);

  const executeWithErrorHandling = useCallback(
    async <T>(
      asyncFn: () => Promise<T>,
      successMessage?: string,
      context = "Operação"
    ): Promise<T | null> => {
      try {
        setIsLoading(true);
        clearError();

        const result = await asyncFn();

        if (successMessage) {
          toast.success(successMessage, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }

        return result;
      } catch (error) {
        handleError(error, context);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [handleError, clearError]
  );

  return {
    error,
    isLoading,
    clearError,
    handleError,
    executeWithErrorHandling,
  };
};
